import React, { useState } from 'react';
import { Text, Button, Input, Label, Spinner, makeStyles } from '@fluentui/react-components';
import { useNavigate } from 'react-router';
import { authClient } from '../utils/auth-client';

const useStyles = makeStyles({
  root: {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fafafa',
  },
  card: {
    width: '400px',
    backgroundColor: '#ffffff',
    padding: '32px',
    borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
});

export const TwoFactorPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'totp' | 'backup'>('totp');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = mode === 'totp'
        ? await (authClient as any).twoFactor.verifyTotp({ code: code.trim() })
        : await (authClient as any).twoFactor.verifyBackupCode({ code: code.trim() });
      if (result?.error) {
        setError(result.error.message?.trim() || 'Code wurde nicht akzeptiert.');
        return;
      }
      window.location.href = '/';
    } catch (err) {
      const message = err instanceof Error ? err.message?.trim() : '';
      setError(message || 'Bestätigung fehlgeschlagen.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <form className={styles.card} onSubmit={submit}>
        <Text size={600} weight="semibold">Zweiter Faktor erforderlich</Text>
        <Text size={300} style={{ color: '#605e5c' }}>
          {mode === 'totp'
            ? 'Bitte gib den 6-stelligen Code aus deiner Authenticator-App ein.'
            : 'Bitte gib einen deiner Backup-Codes ein.'}
        </Text>

        {error && (
          <div style={{ padding: '12px', backgroundColor: '#fde7e9', color: '#a80000', borderRadius: '4px', textAlign: 'center' }}>
            <Text>{error}</Text>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Label htmlFor="codeInput" size="small">{mode === 'totp' ? 'TOTP-Code' : 'Backup-Code'}</Label>
          <Input
            id="codeInput"
            value={code}
            onChange={(_e, data) => setCode(data.value)}
            required
            disabled={isLoading}
            autoFocus
            inputMode={mode === 'totp' ? 'numeric' : 'text'}
            style={{ height: '40px', letterSpacing: mode === 'totp' ? '0.3em' : 'normal' }}
          />
        </div>

        <Button type="submit" appearance="primary" disabled={isLoading || !code.trim()}>
          {isLoading ? <Spinner size="tiny" /> : 'Bestätigen'}
        </Button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button appearance="subtle" size="small" onClick={() => setMode(mode === 'totp' ? 'backup' : 'totp')} disabled={isLoading}>
            {mode === 'totp' ? 'Backup-Code verwenden' : 'TOTP-Code verwenden'}
          </Button>
          <Button appearance="subtle" size="small" onClick={() => navigate('/login')} disabled={isLoading}>
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TwoFactorPage;
